import { useEffect, useState } from 'react';
import { useAppStore } from '../state/store';

// ─── Live Clock ───────────────────────────────────────────────────────────────
function useClock() {
  const [time, setTime] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

function formatClock(d: Date) {
  return d.toISOString().replace('T', ' ').substring(0, 19) + 'Z';
}

// ─── Metric pill ─────────────────────────────────────────────────────────────
function Metric({ label, value, warn, alert }: { label: string; value: string | number; warn?: boolean; alert?: boolean }) {
  const valueColor = alert
    ? 'text-neon-red'
    : warn
    ? 'text-neon-amber'
    : 'text-neon-green';
  return (
    <span className="flex flex-col items-center leading-none gap-0.5 min-w-[40px]">
      <span className={`font-mono text-[12px] font-bold tabular-nums ${valueColor}`}
            style={{ textShadow: alert ? '0 0 6px rgba(255,48,48,0.7)' : warn ? '0 0 6px rgba(255,183,0,0.7)' : '0 0 6px rgba(0,255,65,0.7)' }}>
        {value}
      </span>
      <span className="font-mono text-[8px] tracking-widest text-neon-green/40 uppercase">
        {label}
      </span>
    </span>
  );
}

// ─── Separator ───────────────────────────────────────────────────────────────
function Sep() {
  return <span className="h-5 w-px bg-neon-green/15 mx-1" />;
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ label, active }: { label: string; active?: boolean }) {
  return (
    <span className={[
      'inline-flex items-center px-1.5 py-0.5 font-mono text-[9px] tracking-widest uppercase border',
      active
        ? 'border-neon-green/60 bg-neon-green/10 text-neon-green'
        : 'border-neon-green/20 bg-transparent text-neon-green/25',
    ].join(' ')}>
      {label}
    </span>
  );
}

// ─── Topbar ───────────────────────────────────────────────────────────────────
export function Topbar() {
  const { accessToken, username, logout, listings, messages, approvals, bots, budget } = useAppStore();
  const clock = useClock();

  const activeListings  = listings.filter((l) => l.status === 'active').length;
  const pendingAppr     = approvals.filter((a) => a.status === 'pending').length;
  const unreadMessages  = messages.filter((m) => m.replyStatus === 'pending').length;
  const activeBots      = bots.filter((b) => b.status === 'working').length;
  const totalBots       = bots.length;

  const grossRevCents   = listings
    .filter((l) => l.status === 'active')
    .reduce((sum, l) => sum + l.priceCents, 0);
  const grossRev        = (grossRevCents / 100).toFixed(0);

  const budgetPosture   = budget?.posture ?? 'green';
  const postureColor    = budgetPosture === 'green'
    ? 'bg-neon-green'
    : budgetPosture === 'yellow'
    ? 'bg-neon-amber'
    : 'bg-neon-red';

  const schedulerOnline = activeBots > 0;
  const systemOnline    = !!accessToken;

  return (
    <header
      className="relative flex h-11 shrink-0 items-center justify-between px-4 overflow-hidden"
      style={{
        background: 'rgba(0,6,0,0.97)',
        borderBottom: '1px solid rgba(0,255,65,0.35)',
        boxShadow: '0 1px 0 rgba(0,255,65,0.1), 0 2px 12px rgba(0,0,0,0.8)',
      }}
    >
      {/* Scanline texture */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent 0px, transparent 3px, rgba(0,255,65,0.4) 3px, rgba(0,255,65,0.4) 4px)',
        }}
      />

      {/* Top accent line */}
      <div
        className="pointer-events-none absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(0,255,65,0.6) 20%, rgba(0,255,65,0.9) 50%, rgba(0,255,65,0.6) 80%, transparent)' }}
      />

      {/* ── Left: Designation ── */}
      <div className="flex items-center gap-3 z-10">
        {/* Pulsing status dot */}
        <span className="relative flex h-2 w-2 shrink-0">
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-50 ${systemOnline ? 'bg-neon-green' : 'bg-neon-green/30'}`} />
          <span className={`relative inline-flex rounded-full h-2 w-2 ${systemOnline ? 'bg-neon-green' : 'bg-neon-green/30'}`}
                style={systemOnline ? { boxShadow: '0 0 6px rgba(0,255,65,0.9)' } : {}} />
        </span>

        <div className="flex flex-col leading-none gap-0.5">
          <span className="font-mono text-[12px] tracking-[0.22em] text-neon-green uppercase font-bold"
                style={{ textShadow: '0 0 8px rgba(0,255,65,0.7)' }}>
            THE·HORIZON
          </span>
          <span className="font-mono text-[8px] tracking-[0.2em] text-neon-green/35 uppercase">
            OPS·CMD·ALPHA&nbsp;//&nbsp;v1.0
          </span>
        </div>

        <Sep />

        {/* Channel badges */}
        <div className="hidden sm:flex items-center gap-1">
          <StatusBadge label="eBay"   active={true} />
          <StatusBadge label="Etsy"   active={true} />
          <StatusBadge label="POD"    active={true} />
        </div>
      </div>

      {/* ── Center: Live Metrics ── */}
      {accessToken && (
        <div className="hidden md:flex items-center gap-3 z-10">
          <Metric label="Listings" value={activeListings} />
          <Sep />
          <Metric label="GMV" value={`$${grossRev}`} />
          <Sep />
          <Metric label="Msgs" value={unreadMessages} warn={unreadMessages > 0} />
          <Sep />
          <Metric label="Queue" value={pendingAppr} alert={pendingAppr > 0} />
          <Sep />
          <Metric label={`${activeBots}/${totalBots} Agents`} value={activeBots > 0 ? '●ACTIVE' : '○STBY'} warn={activeBots > 0} />
        </div>
      )}

      {/* ── Right: Clock + Auth ── */}
      <div className="flex items-center gap-2 z-10">
        {/* Live clock */}
        <div className="hidden lg:flex flex-col items-end leading-none gap-0.5">
          <span className="font-mono text-[10px] tabular-nums text-neon-green/70 tracking-wider">
            {formatClock(clock)}
          </span>
          <span className="font-mono text-[7px] tracking-widest text-neon-green/30 uppercase">
            SYS·TIME·UTC
          </span>
        </div>

        <Sep />

        {/* System posture indicator */}
        <div className="flex items-center gap-1.5">
          <span className={`h-1.5 w-1.5 rounded-full ${postureColor} ${schedulerOnline ? 'animate-pulse' : ''}`}
                style={{ boxShadow: schedulerOnline ? '0 0 6px currentColor' : 'none' }} />
          <span className="font-mono text-[9px] tracking-wider text-neon-green/40 uppercase hidden sm:inline">
            {accessToken ? (schedulerOnline ? 'SYS·ONLINE' : 'STANDBY') : 'AWAITING·AUTH'}
          </span>
        </div>

        {accessToken && (
          <>
            <Sep />
            <span className="font-mono text-[9px] text-neon-green/50 tracking-wider hidden sm:inline uppercase">
              {username ?? 'Captain'}
            </span>
            <button
              onClick={() => logout()}
              className="rounded-none border border-neon-green/30 px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-neon-green/40 transition-all hover:border-neon-green/70 hover:bg-neon-green/8 hover:text-neon-green"
              style={{ transition: 'all 0.15s' }}
            >
              [EXIT]
            </button>
          </>
        )}
      </div>

      {/* Bottom accent line */}
      <div
        className="pointer-events-none absolute bottom-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(0,255,65,0.3) 30%, rgba(0,255,65,0.5) 50%, rgba(0,255,65,0.3) 70%, transparent)' }}
      />
    </header>
  );
}
